import React from "react";
import generateEmbedOptions from "../../utils/generateEmbedOptions";
import PropTypes from "prop-types";
import throttle from "lodash.throttle";
import seekToAction from "../../utils/seekUtil";
import { generatePosterUrl } from "../../utils/ImageUtils";
import GTMTracker from "../../utils/tracking/GTMTracker";
import { FALLBACK_IMAGE } from "../../constants/Urls";

const PLAYLIST_OPTIONS = {
    loadSuggestions: false,
    transitionDuration: 10,
};
const BOUND_METHODS = ["_onTranscriptReady", "_onTimeUpdate"];

export default class VideoPlayer extends React.Component {
    constructor(...args) {
        super(...args);
        this.poster = this.props.videoStory.video.thumbnail.url || "";
        this.enableSkylight = this.props.enableSkylight;
        BOUND_METHODS.forEach(
            (methodName) => (this[methodName] = this[methodName].bind(this))
        );
        this._onTimeUpdate = throttle(this._onTimeUpdate, 500);
    }

    render() {
        return (
            <div>
                <div className="video-player">
                    <div
                        className={"video-player__container"}
                        ref={(playerContainer) =>
                            (this.playerContainer = playerContainer)
                        }
                        id="horizontal-video-player"
                    >
                        {this.poster && (
                            <video
                                controls={false}
                                // default poster to placeholder to avoid bad cropping that's fixed in media-ui player
                                poster={generatePosterUrl(
                                    this.props.videoStory.isVertical
                                        ? FALLBACK_IMAGE
                                        : this.poster
                                )}
                                className="poster-video"
                            >
                                <source src="" type="video/webm" />
                                <track default kind="captions" src="" />
                            </video>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // todo ENG4VIDEO-4929 clean up this code as part of playlist implementation
    buildSkylight(assetId) {
        const { videoEmbedServiceEnv } = this.props;

        const onReady = (player) =>
            player.on("ended", () =>
                console.log(
                    ">>> player ended, switch to the next stream once api options are available"
                )
            );

        const skylightRef =
            window.skylightFullBundle.createHorizontalVideoPlayer({
                id: "horizontal-video-player",
                horizontalPlayerProps: {
                    assetId,
                    // todo ENG4VIDEO-4935 turn on autoplay here
                    skylightOptions: {
                        enableDai: true,
                        autoplay: false,
                        serviceEnv: videoEmbedServiceEnv,
                    },
                    options: {
                        poster: generatePosterUrl(
                            this.props.videoStory.isVertical
                                ? FALLBACK_IMAGE
                                : this.poster
                        ),
                    },
                    onReady,
                },
            });

        this.player = skylightRef.current;
    }

    componentDidUpdate(prevProps) {
        const { videoList, bmmrId, isTranscriptOn } = this.props;

        // todo ENG4VIDEO-4929 clean up this code as part of playlist implementation
        if (this.enableSkylight) {
            return;
        }

        const videoListChanged = this._isVideoListDifferent(
            prevProps.videoList
        );

        if (videoListChanged) {
            const playlistVideos = videoList.map((videoStory) =>
                this._playerOptionsFromVideoStory(videoStory)
            );
            this.player.resetPlaylist(playlistVideos);
        }

        const durationInSec =
            Number.parseInt(getVideoDuration(videoList[0])) / 1000;

        // this.player.getCurrentId() was added to handle the playlist player autoplay case,
        // so that we don't end up playing 2 videos simultaneously
        const videoContentChanged =
            prevProps.bmmrId !== bmmrId &&
            this.player.getCurrentId() !== bmmrId;
        if (videoContentChanged) {
            this.player.playId(bmmrId);
        }

        if (isTranscriptOn) {
            this.player.transcriptOn();
        } else {
            this.player.transcriptOff();
        }

        this.player.ready(() => {
            seekToAction(this.playerContainer, durationInSec);
        });
    }

    componentDidMount() {
        if (this.enableSkylight) {
            this._createSkylightPlayer();
        } else {
            this._createPlayer();
        }
    }

    componentWillUnmount() {
        this._disposePlayer();
    }

    _createPlayer() {
        const {
            videoList,
            bmmrId,
            onTranscriptOn,
            onTranscriptOff,
            onVideoChange,
            videoStory,
        } = this.props;

        const actualVideoList = videoList.some(
            ({ video: { bmmrId: currentBmmrId } }) => currentBmmrId === bmmrId
        )
            ? videoList
            : [videoStory, ...videoList];
        const playlistVideos = actualVideoList.map((actualVideoStory) =>
            this._playerOptionsFromVideoStory(actualVideoStory)
        );

        let startIndex = 0;
        for (let i = 0; i < playlistVideos.length; i++) {
            if (bmmrId === playlistVideos[i].id) {
                startIndex = i;
                break;
            }
        }

        const playlistOptions = Object.assign(
            {
                startFrom: startIndex,
            },
            PLAYLIST_OPTIONS
        );

        this.player = window.BPlayer.createPlaylistPlayer(
            this.playerContainer,
            playlistVideos,
            playlistOptions
        );

        this.player.on(
            "SLIDE_CLICK",
            this._onVideoSelected.bind(this, "up_next")
        );
        this.player.on(
            "TRANSITION_END",
            this._onVideoContinuousPlay.bind(this)
        );
        this.player.on(
            "TRANSITION_SKIP",
            this._onVideoSelected.bind(this, "transition_skip")
        );
        this.player.on("transcriptOn", onTranscriptOn);
        this.player.on("transcriptOff", onTranscriptOff);
        this.player.on("videoChange", onVideoChange);
        this.player.on("transcriptReady", this._onTranscriptReady);
        this.player.on("timeupdate", this._onTimeUpdate);
    }

    // todo ENG4VIDEO-4929 clean up this code as part of playlist implementation
    _createSkylightPlayer() {
        const {
            videoList,
            bmmrId,
            // onTranscriptOn,
            // onTranscriptOff,
            // onVideoChange,
            videoStory,
        } = this.props;

        const actualVideoList = videoList.some(
            ({ video: { bmmrId: currentBmmrId } }) => currentBmmrId === bmmrId
        )
            ? videoList
            : [videoStory, ...videoList];
        const playlistVideos = actualVideoList.map((actualVideoStory) =>
            this._playerOptionsFromVideoStory(actualVideoStory)
        );

        let startIndex = 0;
        for (let i = 0; i < playlistVideos.length; i++) {
            if (bmmrId === playlistVideos[i].id) {
                startIndex = i;
                break;
            }
        }

        this.buildSkylight(playlistVideos[startIndex].id);
    }

    _onTranscriptReady() {
        this.props.onTranscriptReady(this.player.transcript());
    }

    _onTimeUpdate() {
        this.props.onTimeUpdate(this.player.currentTime());
    }

    _onVideoSelected(label, event) {
        const bmmrId = event.detail.id;
        this.setState({ bmmrId });

        GTMTracker.trackEngagement({
            bvpEventAction: "click",
            bvpEventLabel: `${this.props.eventPrefix}.${label}`,
        });

        this.props.onPlaylistSelected(bmmrId);
    }

    _onVideoContinuousPlay(event) {
        const bmmrId = event.detail.id;
        this.setState({ bmmrId });
        this.props.onPlaylistContinuousPlay(bmmrId);
    }

    _disposePlayer() {
        if (this.player) {
            this.player.dispose();
            this.player = null;
        }
    }

    _isVideoListDifferent(nextVideoList) {
        const currentLength = this.props.videoList.length;
        const nextLength = nextVideoList.length;
        if (currentLength !== nextLength) {
            return true;
        }

        for (let i = 0; i < currentLength; i++) {
            const currentVideo = this.props.videoList[i];
            const nextVideo = nextVideoList[i];
            if (currentVideo.video.bmmrId !== nextVideo.video.bmmrId) {
                return true;
            }
        }

        return false;
    }

    _playerOptionsFromVideoStory(videoStory) {
        const {
            playerId,
            canonicalUrl,
            autoplay,
            continuousPlayPreviousUrl,
            site,
            videoEmbedServiceEnv,
        } = this.props;

        return generateEmbedOptions({
            bmmrId: videoStory.video.bmmrId,
            playerId,
            canonicalUrl,
            autoplay,
            continuousPlayPreviousUrl,
            serviceEnv: videoEmbedServiceEnv,
            title: videoStory.headline.html,
            // default poster to placeholder to avoid bad cropping that's fixed in media-ui player
            poster: videoStory.isVertical
                ? FALLBACK_IMAGE
                : videoStory.video.thumbnail?.url,
            duration: videoStory.video.duration,
            site,
        });
    }
}

function getVideoDuration(videoStory) {
    return videoStory?.video.duration;
}

VideoPlayer.defaultProps = {
    videoList: [],
    onPlaylistSelected: () => {},
    onPlaylistContinuousPlay: () => {},
    onTranscriptOn: () => {},
    onTranscriptOff: () => {},
    onTranscriptReady: () => {},
    onVideoChange: () => {},
    onTimeUpdate: () => {},
    eventPrefix: "",
    videoEmbedServiceEnv: "",
    enableSkylight: false,
};

VideoPlayer.propTypes = {
    bmmrId: PropTypes.string.isRequired,
    playerId: PropTypes.string.isRequired,
    canonicalUrl: PropTypes.string,
    autoplay: PropTypes.bool,
    continuousPlayPreviousUrl: PropTypes.string,
    videoList: PropTypes.array,
    onPlaylistSelected: PropTypes.func,
    onPlaylistContinuousPlay: PropTypes.func,
    eventPrefix: PropTypes.string,
    site: PropTypes.string,
    onTranscriptOn: PropTypes.func,
    onTranscriptOff: PropTypes.func,
    onVideoChange: PropTypes.func,
    onTranscriptReady: PropTypes.func,
    onTimeUpdate: PropTypes.func,
    isTranscriptOn: PropTypes.bool,
    videoStory: PropTypes.object.isRequired,
    videoEmbedServiceEnv: PropTypes.string,
    enableSkylight: PropTypes.bool,
};
